import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, X, Percent, Leaf, Tag } from "lucide-react";

export default function CatalogFees({ canEdit }) {
  const [f, setF] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/catalog/settings").then((r) => setF(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/catalog/settings", {
        tax_pct: Number(f.tax_pct) || 0,
        environmental_pct: Number(f.environmental_pct) || 0,
        environmental_basis: "equipment",
        equipment_categories: f.equipment_categories || [],
      });
      toast.success("Catalog fees saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const addCat = () => {
    const c = newCat.trim();
    if (!c) return;
    if ((f.equipment_categories || []).includes(c)) { toast.error("Already in the list"); return; }
    set("equipment_categories", [...(f.equipment_categories || []), c]);
    setNewCat("");
  };
  const delCat = (c) => set("equipment_categories", (f.equipment_categories || []).filter((x) => x !== c));

  if (!f) return <div className="text-sm text-slate-400 py-6">Loading fees…</div>;

  return (
    <div className="space-y-4" data-testid="catalog-fees">
      <p className="text-xs text-slate-500">Global, catalog-wide values — applied to every bid the same way, with no per-project or per-item override. Fuel stays county-derived from each project's location.</p>

      <Card className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-3">Global fees</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs flex items-center gap-1.5"><Percent className="w-3.5 h-3.5 text-[#003E87]" /> Sales / use tax %</Label>
            <Input type="number" step="any" disabled={!canEdit} value={f.tax_pct ?? ""} onChange={(e) => set("tax_pct", e.target.value)} className="mt-1 font-mono" data-testid="fees-tax" placeholder="0" />
            <p className="text-[10px] text-slate-400 mt-1">Applied to taxable material + equipment across the catalog.</p>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5"><Leaf className="w-3.5 h-3.5 text-emerald-600" /> Environmental fee %</Label>
            <Input type="number" step="any" disabled={!canEdit} value={f.environmental_pct ?? ""} onChange={(e) => set("environmental_pct", e.target.value)} className="mt-1 font-mono" data-testid="fees-env" placeholder="0" />
            <p className="text-[10px] text-slate-400 mt-1">An equipment-supplier surcharge — charged on equipment only, never on material or labor.</p>
          </div>
          <div>
            <Label className="text-xs">Environmental fee basis</Label>
            <div className="w-full mt-1 h-10 flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 rounded px-2.5 bg-slate-50" data-testid="fees-env-basis">
              <Leaf className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> Equipment only
            </div>
            <p className="text-[10px] text-slate-400 mt-1">No equipment on a bid → no environmental fee.</p>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Equipment categories</div>
        <p className="text-[11px] text-slate-500 mb-3">The managed list estimators pick from when adding equipment (e.g. Skid Steer → Earthwork).</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(f.equipment_categories || []).map((c) => (
            <span key={c} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-3 pr-1.5 py-1" data-testid={`fees-cat-${c}`}>
              {c}{canEdit && <button onClick={() => delCat(c)} className="text-slate-400 hover:text-rose-600" data-testid={`fees-cat-del-${c}`}><X className="w-3 h-3" /></button>}
            </span>
          ))}
          {(f.equipment_categories || []).length === 0 && <span className="text-xs text-slate-400 italic">No categories yet.</span>}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 max-w-sm">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="Add a category…" className="h-9" data-testid="fees-cat-input" />
            <Button size="sm" variant="outline" onClick={addCat} data-testid="fees-cat-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
          </div>
        )}
      </Card>

      {canEdit && <div className="flex justify-end"><Button onClick={save} disabled={saving} className="bg-[#003E87] hover:bg-[#00306a]" data-testid="fees-save">Save catalog settings</Button></div>}
    </div>
  );
}
